import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { db } from "../../firebase";
import { fmtInr, fmtInrPdf } from "../../utils/eventUtils";

const palette = {
  blue: [25, 118, 210],
  lightBlue: [227, 242, 253],
  white: [255, 255, 255],
  black: [0, 0, 0],
  green: [46, 125, 50],
  red: [198, 40, 40],
};

const PDF_LAYOUT = {
  topMargin: 40,
  leftMargin: 35,
  rightMargin: 35,
  bottomMargin: 75,
  sectionGap: 18,
};

const normalizeValue = (value) => String(value ?? "").trim().toLowerCase();

const compareStrings = (firstValue, secondValue) =>
  String(firstValue || "").localeCompare(String(secondValue || ""), undefined, {
    sensitivity: "base",
    numeric: true,
  });

const getTimestampDate = (value) => {
  try {
    if (value && typeof value.toDate === "function") return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === "number" || typeof value === "string") {
      const convertedDate = new Date(value);
      if (!Number.isNaN(convertedDate.getTime())) return convertedDate;
    }
  } catch (error) {
    console.error("Date conversion error:", error);
  }
  return null;
};

const formatTimestamp = (value) => {
  const date = getTimestampDate(value);
  return date ? date.toLocaleString("en-IN") : "-";
};

const formatDateOnly = (value) => {
  const date = getTimestampDate(value);

  return date
    ? date.toLocaleDateString("en-IN")
    : "-";
};

const sanitizeFileName = (value) =>
  String(value || "Event")
    .trim()
    .replace(/[<>:"/\\|?*]+/g, "_")
    .replace(/\s+/g, "_");

const getResidentIdentity = (resident) => ({
  uid: normalizeValue(
    resident?.uid || resident?.userId || resident?.residentUid || resident?.id
  ),
  email: normalizeValue(
    resident?.email || resident?.emailId || resident?.userEmail
  ),
  name: normalizeValue(
    resident?.fullName ||
      resident?.displayName ||
      resident?.residentName ||
      resident?.name
  ),
  blockName: normalizeValue(
    resident?.unit?.blockName ||
      resident?.flat?.blockName ||
      resident?.blockName ||
      resident?.block
  ),
  flatLabel: normalizeValue(
    resident?.unit?.flatLabel ||
      resident?.flat?.flatLabel ||
      resident?.flatLabel ||
      resident?.flatNumber ||
      resident?.unitNumber
  ),
});

const getFundContributorIdentity = (fund) => ({
  uid: normalizeValue(
    fund?.residentUid ||
      fund?.contributorUid ||
      fund?.paidByUid ||
      fund?.uid ||
      fund?.userId
  ),
  email: normalizeValue(
    fund?.residentEmail ||
      fund?.contributorEmail ||
      fund?.paidByEmail ||
      fund?.email
  ),
  name: normalizeValue(
    fund?.residentName || fund?.contributorName || fund?.paidByName
  ),
  blockName: normalizeValue(
    fund?.blockName || fund?.block || fund?.unit?.blockName
  ),
  flatLabel: normalizeValue(
    fund?.flatLabel ||
      fund?.flatNumber ||
      fund?.unitNumber ||
      fund?.unit?.flatLabel
  ),
});

const isIdentityMatch = (firstIdentity, secondIdentity) => {
  const matchesByUid =
    Boolean(firstIdentity.uid) &&
    Boolean(secondIdentity.uid) &&
    firstIdentity.uid === secondIdentity.uid;

  const matchesByEmail =
    Boolean(firstIdentity.email) &&
    Boolean(secondIdentity.email) &&
    firstIdentity.email === secondIdentity.email;

  const matchesByName =
    Boolean(firstIdentity.name) &&
    Boolean(secondIdentity.name) &&
    firstIdentity.name === secondIdentity.name;

  const matchesByUnit =
    Boolean(firstIdentity.blockName) &&
    Boolean(firstIdentity.flatLabel) &&
    Boolean(secondIdentity.blockName) &&
    Boolean(secondIdentity.flatLabel) &&
    firstIdentity.blockName === secondIdentity.blockName &&
    firstIdentity.flatLabel === secondIdentity.flatLabel;

  return matchesByUid || matchesByEmail || matchesByName || matchesByUnit;
};

const getBlockName = (item) =>
  String(
    item?.blockName ||
      item?.block ||
      item?.unit?.blockName ||
      item?.expenseBlockName ||
      ""
  ).trim();

const getExternalContributorName = (fund) =>
  fund?.contributorName || fund?.paidByName || fund?.residentName || "-";

const getContributionType = (fund) =>
  fund?.otherTitle || fund?.otherType || "Other";

export default function OverviewPanel({
  event,
  community,
  residents = [],
  currentResident,
  canEdit,
}) {
  const eventId = event?.id;

  const [funds, setFunds] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loadingFunds, setLoadingFunds] = useState(true);
  const [loadingExpenses, setLoadingExpenses] = useState(true);
  const [fundsError, setFundsError] = useState("");
  const [expensesError, setExpensesError] = useState("");
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!eventId) {
      setFunds([]);
      setExpenses([]);
      setLoadingFunds(false);
      setLoadingExpenses(false);
      setFundsError("Missing event ID.");
      setExpensesError("");
      return undefined;
    }

    setLoadingFunds(true);
    setLoadingExpenses(true);
    setFundsError("");
    setExpensesError("");

    const fundsQuery = query(
      collection(db, "events", eventId, "funds"),
      orderBy("createdAt", "desc")
    );

    const expensesQuery = query(
      collection(db, "events", eventId, "expenses"),
      orderBy("createdAt", "desc")
    );

    const unsubscribeFunds = onSnapshot(
      fundsQuery,
      (snapshot) => {
        setFunds(
          snapshot.docs.map((documentSnapshot) => ({
            id: documentSnapshot.id,
            ...documentSnapshot.data(),
          }))
        );
        setLoadingFunds(false);
      },
      (snapshotError) => {
        console.error("Funds snapshot error:", snapshotError);
        setFunds([]);
        setFundsError(snapshotError?.message || "Failed to load funds.");
        setLoadingFunds(false);
      }
    );

    const unsubscribeExpenses = onSnapshot(
      expensesQuery,
      (snapshot) => {
        setExpenses(
          snapshot.docs.map((documentSnapshot) => ({
            id: documentSnapshot.id,
            ...documentSnapshot.data(),
          }))
        );
        setLoadingExpenses(false);
      },
      (snapshotError) => {
        console.error("Expenses snapshot error:", snapshotError);
        setExpenses([]);
        setExpensesError(snapshotError?.message || "Failed to load expenses.");
        setLoadingExpenses(false);
      }
    );

    return () => {
      unsubscribeFunds();
      unsubscribeExpenses();
    };
  }, [eventId]);

  const residentFunds = useMemo(
    () =>
      funds.filter(
        (fund) =>
          normalizeValue(fund.sourceType || "resident") === "resident"
      ),
    [funds]
  );

  const otherFunds = useMemo(
    () =>
      funds.filter((fund) => normalizeValue(fund.sourceType) === "other"),
    [funds]
  );

  const isExternalContribution = (fund) => {
    const payerType = normalizeValue(fund?.payerType);
    const blockName = getBlockName(fund);
    return payerType === "other" || !blockName;
  };

  const residentBlockContributions = useMemo(
    () => otherFunds.filter((fund) => !isExternalContribution(fund)),
    [otherFunds]
  );

  const externalContributions = useMemo(
    () => otherFunds.filter((fund) => isExternalContribution(fund)),
    [otherFunds]
  );

  const blockWiseCollectionSummary = useMemo(() => {
    const blocks = new Map();

    const ensureBlock = (blockName) => {
      const displayBlockName = String(blockName || "").trim();
      if (!displayBlockName) return null;

      const blockKey = normalizeValue(displayBlockName);

      if (!blocks.has(blockKey)) {
        blocks.set(blockKey, {
          blockName: displayBlockName,
          residentAmount: 0,
          donationAmount: 0,
          total: 0,
        });
      }

      return blocks.get(blockKey);
    };

    residentFunds.forEach((fund) => {
      const row = ensureBlock(getBlockName(fund));
      if (row) row.residentAmount += Number(fund.amount) || 0;
    });

    residentBlockContributions.forEach((fund) => {
      const row = ensureBlock(getBlockName(fund));
      if (row) row.donationAmount += Number(fund.amount) || 0;
    });

    return Array.from(blocks.values())
      .map((row) => ({
        ...row,
        total: row.residentAmount + row.donationAmount,
      }))
      .sort((firstRow, secondRow) =>
        compareStrings(firstRow.blockName, secondRow.blockName)
      );
  }, [residentFunds, residentBlockContributions]);

  const blockResidentGrandTotal = useMemo(
    () =>
      blockWiseCollectionSummary.reduce(
        (total, row) => total + row.residentAmount,
        0
      ),
    [blockWiseCollectionSummary]
  );

  const blockDonationGrandTotal = useMemo(
    () =>
      blockWiseCollectionSummary.reduce(
        (total, row) => total + row.donationAmount,
        0
      ),
    [blockWiseCollectionSummary]
  );

  const blockCollectionGrandTotal = useMemo(
    () =>
      blockWiseCollectionSummary.reduce((total, row) => total + row.total, 0),
    [blockWiseCollectionSummary]
  );

  const externalContributionTotal = useMemo(
    () =>
      externalContributions.reduce(
        (total, fund) => total + (Number(fund.amount) || 0),
        0
      ),
    [externalContributions]
  );

  const totalResidentFunds = useMemo(
    () =>
      residentFunds.reduce(
        (total, fund) => total + (Number(fund.amount) || 0),
        0
      ),
    [residentFunds]
  );

  const totalOtherFunds = useMemo(
    () =>
      otherFunds.reduce(
        (total, fund) => total + (Number(fund.amount) || 0),
        0
      ),
    [otherFunds]
  );

  const totalFunds = totalResidentFunds + totalOtherFunds;

  const totalExpenses = useMemo(
    () =>
      expenses.reduce(
        (total, expense) => total + (Number(expense.amount) || 0),
        0
      ),
    [expenses]
  );

  const balance = totalFunds - totalExpenses;

  const normalizedResidents = useMemo(
    () =>
      Array.isArray(residents)
        ? residents.map((resident) => ({
            resident,
            identity: getResidentIdentity(resident),
          }))
        : [],
    [residents]
  );

  const currentResidentIdentity = useMemo(
    () => getResidentIdentity(currentResident),
    [currentResident]
  );

  const myContributions = useMemo(() => {
    if (!currentResident) return [];

    return funds.filter((fund) => {
      const fundIdentity = getFundContributorIdentity(fund);
      const sourceType = normalizeValue(fund.sourceType || "resident");

      const directMatch = isIdentityMatch(
        currentResidentIdentity,
        fundIdentity
      );

      const residentUnitMatch =
        sourceType === "resident" &&
        Boolean(currentResidentIdentity.blockName) &&
        Boolean(currentResidentIdentity.flatLabel) &&
        currentResidentIdentity.blockName === fundIdentity.blockName &&
        currentResidentIdentity.flatLabel === fundIdentity.flatLabel;

      return directMatch || residentUnitMatch;
    });
  }, [funds, currentResident, currentResidentIdentity]);

  const sortedMyContributions = useMemo(
    () =>
      [...myContributions].sort((firstContribution, secondContribution) => {
        const firstDate =
          getTimestampDate(firstContribution.createdAt)?.getTime() || 0;
        const secondDate =
          getTimestampDate(secondContribution.createdAt)?.getTime() || 0;
        return secondDate - firstDate;
      }),
    [myContributions]
  );

  const myContributionTotal = useMemo(
    () =>
      myContributions.reduce(
        (total, contribution) =>
          total + (Number(contribution.amount) || 0),
        0
      ),
    [myContributions]
  );

  const residentFundsByBlock = useMemo(() => {
    const groups = new Map();

    residentFunds.forEach((fund) => {
      const blockName = getBlockName(fund) || "Other";
      const key = normalizeValue(blockName);
      const group = groups.get(key) || { blockName, funds: [] };
      group.funds.push(fund);
      groups.set(key, group);
    });

    return Array.from(groups.values()).sort((firstGroup, secondGroup) =>
      compareStrings(firstGroup.blockName, secondGroup.blockName)
    );
  }, [residentFunds]);

  const expensesByCategory = useMemo(() => {
    const groups = new Map();

    expenses.forEach((expense) => {
      const category = String(expense.category || "Uncategorized").trim();
      const key = normalizeValue(category);
      const group = groups.get(key) || { category, expenses: [] };
      group.expenses.push(expense);
      groups.set(key, group);
    });

    return Array.from(groups.values()).sort((firstGroup, secondGroup) =>
      compareStrings(firstGroup.category, secondGroup.category)
    );
  }, [expenses]);

  const committeeByBlock = useMemo(() => {
    const groups = new Map();
    const committeeMembers = Array.isArray(event?.committee)
      ? event.committee
      : [];

    committeeMembers.forEach((member) => {
      const blockName = getBlockName(member) || "Other";
      const key = normalizeValue(blockName);
      const group = groups.get(key) || { blockName, members: [] };
      group.members.push(member);
      groups.set(key, group);
    });

    return Array.from(groups.values()).sort((firstGroup, secondGroup) =>
      compareStrings(firstGroup.blockName, secondGroup.blockName)
    );
  }, [event?.committee]);

  const getEventUrl = () => {
    if (event?.publicUrl) return event.publicUrl;

    const communityReference = community?.id || community?.slug || "UNKNOWN";

    return `${window.location.origin}/community/${encodeURIComponent(
      communityReference
    )}/events/${encodeURIComponent(eventId || "")}`;
  };

  const ensurePdfSpace = (pdfDocument, currentY, requiredHeight = 130) => {
    const pageHeight = pdfDocument.internal.pageSize.getHeight();

    if (currentY + requiredHeight > pageHeight - PDF_LAYOUT.bottomMargin) {
      pdfDocument.addPage();
      return PDF_LAYOUT.topMargin;
    }

    return currentY;
  };

  const writeHeader = (pdfDocument) => {
    const pageWidth = pdfDocument.internal.pageSize.getWidth();

    pdfDocument.setFont("helvetica", "bold");
    pdfDocument.setFontSize(16);
    pdfDocument.text(`${event?.name || "Event"} - Summary`, 40, 40);

    pdfDocument.setFont("helvetica", "normal");
    pdfDocument.setFontSize(11);

    let currentY = 64;
    pdfDocument.text(`Community: ${community?.name || "-"}`, 40, currentY);
    currentY += 16;
    pdfDocument.text(`Date: ${event?.date || "-"}`, 40, currentY);
    currentY += 16;

    const descriptionLines = pdfDocument.splitTextToSize(
      `Description: ${event?.description || "-"}`,
      pageWidth - 80
    );

    pdfDocument.text(descriptionLines, 40, currentY);
    return currentY + descriptionLines.length * 14 + 10;
  };

  const writeSummaryBand = (pdfDocument, startingY) => {
    const pageWidth = pdfDocument.internal.pageSize.getWidth();
    const bandX = 40;
    const bandY = startingY + 10;
    const bandWidth = pageWidth - 80;
    const bandHeight = 70;
    const columnWidth = bandWidth / 3;

    pdfDocument.setFillColor(...palette.lightBlue);
    pdfDocument.roundedRect(bandX, bandY, bandWidth, bandHeight, 8, 8, "F");

    const headings = ["Total Funds", "Total Expenses", "Balance"];
    const values = [totalFunds, totalExpenses, balance];

    headings.forEach((heading, index) => {
      const centerX = bandX + columnWidth * index + columnWidth / 2;

      pdfDocument.setFont("helvetica", "normal");
      pdfDocument.setFontSize(10);
      pdfDocument.setTextColor(100);
      pdfDocument.text(heading, centerX, bandY + 22, { align: "center" });

      pdfDocument.setFont("helvetica", "bold");
      pdfDocument.setFontSize(12);
      pdfDocument.setTextColor(
        ...(index === 1 || (index === 2 && balance < 0)
          ? palette.red
          : palette.green)
      );
      pdfDocument.text(fmtInrPdf(values[index]), centerX, bandY + 46, {
        align: "center",
      });
    });

    pdfDocument.setTextColor(...palette.black);
    return bandY + bandHeight + 20;
  };

  const writeSectionHeading = (pdfDocument, title, startingY) => {
    const headingY = ensurePdfSpace(pdfDocument, startingY, 80);
    pdfDocument.setFont("helvetica", "bold");
    pdfDocument.setFontSize(15);
    pdfDocument.setTextColor(...palette.black);
    pdfDocument.text(title, 40, headingY);
    return headingY + 22;
  };

  const writeBlockWiseSummaryTable = (pdfDocument, startingY) => {
    const titleY = ensurePdfSpace(pdfDocument, startingY, 170);

    pdfDocument.setFont("helvetica", "bold");
    pdfDocument.setFontSize(13);
    pdfDocument.text("Block-wise Collection", PDF_LAYOUT.leftMargin, titleY);

    const body = blockWiseCollectionSummary.length
      ? blockWiseCollectionSummary.map((row) => [
          row.blockName,
          fmtInrPdf(row.residentAmount),
          fmtInrPdf(row.donationAmount),
          fmtInrPdf(row.total),
        ])
      : [
          [
            {
              content: "No block-wise collections found.",
              colSpan: 4,
              styles: { halign: "center" },
            },
          ],
        ];

    autoTable(pdfDocument, {
      startY: titleY + 10,
      head: [
        [
          "Block",
          "Resident Contributions",
          "Donations / Sponsorships",
          "Total",
        ],
      ],
      body,
      foot: blockWiseCollectionSummary.length
        ? [
            [
              "Grand Total",
              fmtInrPdf(blockResidentGrandTotal),
              fmtInrPdf(blockDonationGrandTotal),
              fmtInrPdf(blockCollectionGrandTotal),
            ],
          ]
        : [],
      styles: {
        fontSize: 8,
        cellPadding: 5,
        lineWidth: 0.1,
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        fillColor: palette.blue,
        textColor: palette.white,
        halign: "center",
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 90, halign: "left" },
        1: { cellWidth: 135, halign: "right" },
        2: { cellWidth: 150, halign: "right" },
        3: { cellWidth: 110, halign: "right", fontStyle: "bold" },
      },
      footStyles: {
        fillColor: [240, 248, 240],
        textColor: palette.green,
        fontStyle: "bold",
      },
      theme: "striped",
      margin: {
        top: 55,
        left: PDF_LAYOUT.leftMargin,
        right: PDF_LAYOUT.rightMargin,
        bottom: PDF_LAYOUT.bottomMargin,
      },
      showHead: "everyPage",
      showFoot: "lastPage",
      rowPageBreak: "avoid",
    });

    return pdfDocument.lastAutoTable?.finalY || titleY + 15;
  };

  const writeExternalCollectionsTable = (pdfDocument, startingY) => {
    const titleY = ensurePdfSpace(pdfDocument, startingY, 170);

    pdfDocument.setFont("helvetica", "bold");
    pdfDocument.setFontSize(13);
    pdfDocument.text(
      "External Contributions / Sponsorships",
      PDF_LAYOUT.leftMargin,
      titleY
    );

    const body = externalContributions.length
      ? externalContributions.map((fund) => [
          getExternalContributorName(fund),
          getContributionType(fund),
          fmtInrPdf(Number(fund.amount) || 0),
        ])
      : [
          [
            {
              content: "No external contributions found.",
              colSpan: 3,
              styles: { halign: "center" },
            },
          ],
        ];

    autoTable(pdfDocument, {
      startY: titleY + 10,
      head: [["Contributor", "Type", "Amount"]],
      body,
      foot: externalContributions.length
        ? [
            [
              { content: "Grand Total", colSpan: 2 },
              fmtInrPdf(externalContributionTotal),
            ],
          ]
        : [],
      styles: {
        fontSize: 9,
        cellPadding: 5,
        lineWidth: 0.1,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: palette.blue,
        textColor: palette.white,
        halign: "center",
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 210, halign: "left" },
        1: { cellWidth: 170, halign: "left" },
        2: { cellWidth: 105, halign: "right" },
      },
      footStyles: {
        fillColor: [240, 248, 240],
        textColor: palette.green,
        fontStyle: "bold",
      },
      theme: "striped",
      margin: {
        top: 55,
        left: PDF_LAYOUT.leftMargin,
        right: PDF_LAYOUT.rightMargin,
        bottom: PDF_LAYOUT.bottomMargin,
      },
      showHead: "everyPage",
      showFoot: "lastPage",
      rowPageBreak: "avoid",
    });

    return pdfDocument.lastAutoTable?.finalY || titleY + 15;
  };

  const writeExpensesByCategoryTable = (
    pdfDocument,
    startingY,
    options = {}
  ) => {
    if (options.forceNewPage) pdfDocument.addPage();

    const titleY = startingY;
    pdfDocument.setFont("helvetica", "bold");
    pdfDocument.setFontSize(13);
    pdfDocument.text("Expenses - Category-wise", 40, titleY);

    const body = expensesByCategory.length
      ? expensesByCategory.map((group) => [
          group.category,
          fmtInrPdf(
            group.expenses.reduce(
              (total, expense) => total + (Number(expense.amount) || 0),
              0
            )
          ),
          String(group.expenses.length),
        ])
      : [
          [
            {
              content: "No expenses recorded.",
              colSpan: 3,
              styles: { halign: "center" },
            },
          ],
        ];

    autoTable(pdfDocument, {
      startY: titleY + 10,
      head: [["Category", "Total Amount", "Entries"]],
      body,
      foot: expenses.length
        ? [["Grand Total", fmtInrPdf(totalExpenses), String(expenses.length)]]
        : [],
      styles: { fontSize: 9, cellPadding: 5, lineWidth: 0.1 },
      headStyles: {
        fillColor: palette.blue,
        textColor: palette.white,
        halign: "center",
        fontStyle: "bold",
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right" },
        2: { halign: "center" },
      },
      footStyles: {
        fillColor: [255, 240, 240],
        textColor: palette.red,
        fontStyle: "bold",
      },
      theme: "striped",
      margin: { left: 40, right: 40, bottom: PDF_LAYOUT.bottomMargin },
      showHead: "everyPage",
      showFoot: "lastPage",
    });

    return pdfDocument.lastAutoTable?.finalY || titleY + 15;
  };

  const writeResidentCollectionDetails = (pdfDocument) => {
    pdfDocument.addPage();
    let currentY = PDF_LAYOUT.topMargin;

    pdfDocument.setFont("helvetica", "bold");
    pdfDocument.setFontSize(15);
    pdfDocument.text(
      "Resident Collection Details",
      PDF_LAYOUT.leftMargin,
      currentY
    );
    currentY += 24;

    if (!residentFundsByBlock.length) {
      pdfDocument.setFont("helvetica", "normal");
      pdfDocument.setFontSize(10);
      pdfDocument.text(
        "No resident collections found.",
        PDF_LAYOUT.leftMargin,
        currentY
      );
      return;
    }

    residentFundsByBlock.forEach((group) => {
      currentY = ensurePdfSpace(
        pdfDocument,
        currentY,
        75 + Math.min(group.funds.length, 5) * 25
      );

      const sortedFunds = [...group.funds].sort((firstFund, secondFund) =>
        compareStrings(
          firstFund.flatLabel || firstFund.flatNumber || firstFund.unitNumber,
          secondFund.flatLabel || secondFund.flatNumber || secondFund.unitNumber
        )
      );

      const blockTotal = sortedFunds.reduce(
        (total, fund) => total + (Number(fund.amount) || 0),
        0
      );

      pdfDocument.setFont("helvetica", "bold");
      pdfDocument.setFontSize(12);
      pdfDocument.text(`Block: ${group.blockName}`, PDF_LAYOUT.leftMargin, currentY);

      autoTable(pdfDocument, {
        startY: currentY + 10,
        head: [["Flat", "Amount", "Method", "Received By", "Receipt", "Date"]],
        body: sortedFunds.map((fund) => [
          fund.flatLabel || fund.flatNumber || fund.unitNumber || "-",
          fmtInrPdf(Number(fund.amount) || 0),
          fund.method || "-",
          fund.receivedByName || fund.paidByName || "-",
          fund.receiptNo || "-",
          // formatTimestamp(fund.createdAt),
          formatDateOnly(fund.createdAt)
        ]),
        foot: [["Block Total", fmtInrPdf(blockTotal), "", "", "", ""]],
        styles: {
          fontSize: 8,
          cellPadding: 4,
          lineWidth: 0.1,
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: palette.blue,
          textColor: palette.white,
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 62 },
          1: { cellWidth: 72, halign: "right" },
          2: { cellWidth: 65 },
          3: { cellWidth: 100 },
          4: { cellWidth: 72 },
          5: { cellWidth: 150 },
        },
        footStyles: {
          fillColor: [240, 248, 240],
          textColor: palette.green,
          fontStyle: "bold",
        },
        margin: {
          top: 55,
          left: PDF_LAYOUT.leftMargin,
          right: PDF_LAYOUT.rightMargin,
          bottom: PDF_LAYOUT.bottomMargin,
        },
        theme: "striped",
        showHead: "everyPage",
        showFoot: "lastPage",
        rowPageBreak: "avoid",
      });

      currentY =
        (pdfDocument.lastAutoTable?.finalY || currentY) + PDF_LAYOUT.sectionGap;
    });
  };

  const writeExternalContributionDetails = (pdfDocument) => {
    if (!externalContributions.length) return;

    pdfDocument.addPage();
    const currentY = PDF_LAYOUT.topMargin;

    pdfDocument.setFont("helvetica", "bold");
    pdfDocument.setFontSize(15);
    pdfDocument.text(
      "External Contribution Details",
      PDF_LAYOUT.leftMargin,
      currentY
    );

    const sortedContributions = [...externalContributions].sort(
      (firstContribution, secondContribution) => {
        const firstDate =
          getTimestampDate(firstContribution.createdAt)?.getTime() || 0;
        const secondDate =
          getTimestampDate(secondContribution.createdAt)?.getTime() || 0;
        return secondDate - firstDate;
      }
    );

    autoTable(pdfDocument, {
      startY: currentY + 24,
      head: [
        [
          "Contributor",
          "Type",
          "Amount",
          "Method",
          "Received By",
          "Receipt",
          "Date",
        ],
      ],
      body: sortedContributions.map((fund) => [
        getExternalContributorName(fund),
        getContributionType(fund),
        fmtInrPdf(Number(fund.amount) || 0),
        fund.method || "-",
        fund.receivedByName || fund.receivedByUid || "-",
        fund.receiptNo || "-",
        formatTimestamp(fund.createdAt),
      ]),
      foot: [
        [
          "Grand Total",
          "",
          fmtInrPdf(externalContributionTotal),
          "",
          "",
          "",
          "",
        ],
      ],
      styles: {
        fontSize: 7.5,
        cellPadding: 3.5,
        lineWidth: 0.1,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: palette.blue,
        textColor: palette.white,
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 95 },
        1: { cellWidth: 75 },
        2: { cellWidth: 65, halign: "right" },
        3: { cellWidth: 55 },
        4: { cellWidth: 75 },
        5: { cellWidth: 65 },
        6: { cellWidth: 95 },
      },
      footStyles: {
        fillColor: [240, 248, 240],
        textColor: palette.green,
        fontStyle: "bold",
      },
      margin: { top: 55, left: 32, right: 32, bottom: PDF_LAYOUT.bottomMargin },
      theme: "striped",
      showHead: "everyPage",
      showFoot: "lastPage",
      rowPageBreak: "avoid",
    });
  };


//   const writeExpenseDetails = (pdfDocument) => {
//   pdfDocument.addPage();

//   const currentY = PDF_LAYOUT.topMargin;

//   pdfDocument.setFont("helvetica", "bold");
//   pdfDocument.setFontSize(15);
//   pdfDocument.text(
//     "All Expense Details",
//     PDF_LAYOUT.leftMargin,
//     currentY
//   );

//   const getExpensePaymentDate = (expense) => {
//     const paymentDateValue =
//       expense.paymentDate ||
//       expense.paidDate ||
//       expense.expenseDate ||
//       expense.date;

//     if (!paymentDateValue) {
//       return null;
//     }

//     return getTimestampDate(paymentDateValue);
//   };

//   const formatExpensePaymentDate = (expense) => {
//     const paymentDate = getExpensePaymentDate(expense);

//     if (!paymentDate) {
//       return "-";
//     }

//     return paymentDate.toLocaleDateString("en-IN");
//   };

//   const sortedExpenses = [...expenses].sort(
//     (firstExpense, secondExpense) => {
//       const firstPaymentDate = getExpensePaymentDate(firstExpense);
//       const secondPaymentDate = getExpensePaymentDate(secondExpense);

//       if (!firstPaymentDate && !secondPaymentDate) {
//         return 0;
//       }

//       if (!firstPaymentDate) {
//         return 1;
//       }

//       if (!secondPaymentDate) {
//         return -1;
//       }

//       const dateDifference = [firstPaymentDate.getTime() - secondPaymentDate.getTime()]
//       if (dateDifference !== 0) {
//         return dateDifference
//       }

//       const firstCreatedDate = getTimestampDate(firstExpense.createdAt)?.getTime() || 0;

//       const secondCreatedDate = getTimestampDate(secondExpense.createdAt)?.getTime() || 0;

//       return firstCreatedDate -  secondCreatedDate
//     }
//   );

//   autoTable(pdfDocument, {
//     startY: currentY + 24,

//     head: [
//       [
//         "Payment Date",
//         "Category",
//         "Purpose",
//         "Amount",
//         "Paid By",
//         "Receipt",
//       ],
//     ],

//     body: sortedExpenses.length
//       ? sortedExpenses.map((expense) => [
//           formatExpensePaymentDate(expense),
//           expense.category || "Uncategorized",
//           expense.purpose || "-",
//           fmtInrPdf(Number(expense.amount) || 0),
//           expense.paidByName || expense.paidByUid || "-",
//           expense.receiptNo || "-",
//         ])
//       : [
//           [
//             {
//               content: "No expenses recorded.",
//               colSpan: 6,
//               styles: {
//                 halign: "center",
//               },
//             },
//           ],
//         ],

//     foot: expenses.length
//       ? [
//           [
//             "Total Expenses",
//             "",
//             "",
//             fmtInrPdf(totalExpenses),
//             "",
//             "",
//           ],
//         ]
//       : [],

//     styles: {
//       fontSize: 8,
//       cellPadding: 4,
//       lineWidth: 0.1,
//       overflow: "linebreak",
//       valign: "middle",
//     },

//     headStyles: {
//       fillColor: palette.blue,
//       textColor: palette.white,
//       fontStyle: "bold",
//       halign: "center",
//     },

//     columnStyles: {
//       0: {
//         cellWidth: 70,
//         halign: "center",
//       },
//       1: {
//         cellWidth: 80,
//       },
//       2: {
//         cellWidth: 145,
//       },
//       3: {
//         cellWidth: 70,
//         halign: "right",
//       },
//       4: {
//         cellWidth: 90,
//       },
//       5: {
//         cellWidth: 70,
//       },
//     },

//     footStyles: {
//       fillColor: [255, 240, 240],
//       textColor: palette.red,
//       fontStyle: "bold",
//     },

//     margin: {
//       top: 55,
//       left: PDF_LAYOUT.leftMargin,
//       right: PDF_LAYOUT.rightMargin,
//       bottom: PDF_LAYOUT.bottomMargin,
//     },

//     theme: "striped",
//     showHead: "everyPage",
//     showFoot: "lastPage",
//     rowPageBreak: "avoid",
//   });
// };

const writeExpenseDetails = (pdfDocument) => {
  pdfDocument.addPage();

  const currentY = PDF_LAYOUT.topMargin;

  pdfDocument.setFont("helvetica", "bold");
  pdfDocument.setFontSize(15);
  pdfDocument.text(
    "All Expense Details",
    PDF_LAYOUT.leftMargin,
    currentY
  );

  const getExpensePaymentDate = (expense) => {
    const paymentDateValue =
      expense.paymentDate ||
      expense.paidDate ||
      expense.expenseDate ||
      expense.date;

    if (!paymentDateValue) {
      return null;
    }

    return getTimestampDate(paymentDateValue);
  };

  const formatExpensePaymentDate = (expense) => {
    const paymentDate = getExpensePaymentDate(expense);

    if (!paymentDate) {
      return "-";
    }

    return paymentDate.toLocaleDateString("en-IN");
  };

  const sortedExpenses = [...expenses].sort(
    (firstExpense, secondExpense) => {
      const firstPaymentDate = getExpensePaymentDate(firstExpense);
      const secondPaymentDate = getExpensePaymentDate(secondExpense);

      if (!firstPaymentDate && !secondPaymentDate) {
        return 0;
      }

      if (!firstPaymentDate) {
        return 1;
      }

      if (!secondPaymentDate) {
        return -1;
      }

      const dateDifference =
        firstPaymentDate.getTime() - secondPaymentDate.getTime();

      if (dateDifference !== 0) {
        return dateDifference;
      }

      const firstCreatedDate =
        getTimestampDate(firstExpense.createdAt)?.getTime() || 0;

      const secondCreatedDate =
        getTimestampDate(secondExpense.createdAt)?.getTime() || 0;

      return firstCreatedDate - secondCreatedDate;
    }
  );

  autoTable(pdfDocument, {
    startY: currentY + 24,

    head: [
      [
        "Payment Date",
        "Category",
        "Purpose",
        "Amount",
        "Paid By",
        "Payment Mode",
        "Receipt",
      ],
    ],

    body: sortedExpenses.length
      ? sortedExpenses.map((expense) => [
          formatExpensePaymentDate(expense),
          expense.category || "Uncategorized",
          expense.purpose || "-",
          fmtInrPdf(Number(expense.amount) || 0),
          expense.paidByName || expense.paidByUid || "-",
          expense.paymentMode || expense.method || "-",
          expense.receiptNo || "-",
        ])
      : [
          [
            {
              content: "No expenses recorded.",
              colSpan: 7,
              styles: {
                halign: "center",
              },
            },
          ],
        ],

    foot: expenses.length
      ? [
          [
            "Total Expenses",
            "",
            "",
            fmtInrPdf(totalExpenses),
            "",
            "",
            "",
          ],
        ]
      : [],

    styles: {
      fontSize: 7.5,
      cellPadding: 3.5,
      lineWidth: 0.1,
      overflow: "linebreak",
      valign: "middle",
    },

    headStyles: {
      fillColor: palette.blue,
      textColor: palette.white,
      fontStyle: "bold",
      halign: "center",
    },

    columnStyles: {
      0: {
        cellWidth: 65,
        halign: "center",
      },
      1: {
        cellWidth: 70,
      },
      2: {
        cellWidth: 120,
      },
      3: {
        cellWidth: 65,
        halign: "right",
      },
      4: {
        cellWidth: 80,
      },
      5: {
        cellWidth: 75,
        halign: "center",
      },
      6: {
        cellWidth: 70,
      },
    },

    footStyles: {
      fillColor: [255, 240, 240],
      textColor: palette.red,
      fontStyle: "bold",
    },

    margin: {
      top: 55,
      left: PDF_LAYOUT.leftMargin,
      right: PDF_LAYOUT.rightMargin,
      bottom: PDF_LAYOUT.bottomMargin,
    },

    theme: "striped",
    showHead: "everyPage",
    showFoot: "lastPage",
    rowPageBreak: "avoid",
  });
};
  const writeCommitteeByBlockTable = (pdfDocument) => {
    pdfDocument.addPage();
    let currentY = PDF_LAYOUT.topMargin;

    pdfDocument.setFont("helvetica", "bold");
    pdfDocument.setFontSize(15);
    pdfDocument.text("Committee Members", PDF_LAYOUT.leftMargin, currentY);
    currentY += 24;

    if (!committeeByBlock.length) {
      pdfDocument.setFont("helvetica", "normal");
      pdfDocument.setFontSize(10);
      pdfDocument.text("No committee assigned.", PDF_LAYOUT.leftMargin, currentY);
      return;
    }

    committeeByBlock.forEach((group) => {
      currentY = ensurePdfSpace(
        pdfDocument,
        currentY,
        70 + Math.min(group.members.length, 5) * 25
      );

      pdfDocument.setFont("helvetica", "bold");
      pdfDocument.setFontSize(12);
      pdfDocument.text(`Block: ${group.blockName}`, PDF_LAYOUT.leftMargin, currentY);

      autoTable(pdfDocument, {
        startY: currentY + 8,
        head: [["Resident", "Flat", "Role", "Gender"]],
        body: [...group.members]
          .sort((firstMember, secondMember) =>
            compareStrings(firstMember.flatLabel, secondMember.flatLabel)
          )
          .map((member) => [
            member.residentName ||
              member.fullName ||
              member.name ||
              member.uid ||
              "-",
            member.flatLabel || member.flatNumber || "-",
            member.roleLabel || member.role || "-",
            member.gender || "-",
          ]),
        styles: {
          fontSize: 9,
          cellPadding: 4,
          lineWidth: 0.1,
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: palette.blue,
          textColor: palette.white,
          halign: "center",
          fontStyle: "bold",
        },
        theme: "striped",
        margin: {
          top: 55,
          left: PDF_LAYOUT.leftMargin,
          right: PDF_LAYOUT.rightMargin,
          bottom: PDF_LAYOUT.bottomMargin,
        },
        showHead: "everyPage",
        rowPageBreak: "avoid",
      });

      currentY =
        (pdfDocument.lastAutoTable?.finalY || currentY) + PDF_LAYOUT.sectionGap;
    });
  };

  const addEventLinkToLastPage = (pdfDocument) => {
    const pageHeight = pdfDocument.internal.pageSize.getHeight();
    pdfDocument.setPage(pdfDocument.getNumberOfPages());
    pdfDocument.setFont("helvetica", "bold");
    pdfDocument.setFontSize(9);
    pdfDocument.setTextColor(...palette.blue);

    if (typeof pdfDocument.textWithLink === "function") {
      pdfDocument.textWithLink("View full event details", 40, pageHeight - 35, {
        url: getEventUrl(),
      });
    }

    pdfDocument.setTextColor(...palette.black);
  };

  const addPageNumbers = (pdfDocument) => {
    const pageCount = pdfDocument.getNumberOfPages();

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      pdfDocument.setPage(pageNumber);
      const pageWidth = pdfDocument.internal.pageSize.getWidth();
      const pageHeight = pdfDocument.internal.pageSize.getHeight();
      pdfDocument.setFont("helvetica", "normal");
      pdfDocument.setFontSize(7);
      pdfDocument.setTextColor(120, 120, 120);
      pdfDocument.text(
        `Page ${pageNumber} of ${pageCount}`,
        pageWidth /2,
        pageHeight - 8,
        { align: "center" }
      );
      pdfDocument.setTextColor(...palette.black);
    }
  };

  const addStatementFooter = (pdfDocument) => {
  const pageCount = pdfDocument.getNumberOfPages();

  const communityName =
    String(
      community?.name ||
        community?.communityName ||
        community?.data?.name ||
        ""
    ).trim() || "Community";

  const eventName =
    String(event?.name || event?.title || "").trim() || "Community Event";

  const applicationUrl = "https://lupieapp.com";
  const applicationUrlText = "lupieapp.com";

  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    pdfDocument.setPage(pageNumber);

    const pageWidth = pdfDocument.internal.pageSize.getWidth();
    const pageHeight = pdfDocument.internal.pageSize.getHeight();

    const footerLeft = PDF_LAYOUT.leftMargin;
    const footerRight = pageWidth - PDF_LAYOUT.rightMargin;
    const footerLineY = pageHeight - 32;

    pdfDocument.setFont("helvetica", "normal");
    pdfDocument.setFontSize(8);
    pdfDocument.setTextColor(100, 100, 100);

    // pdfDocument.text(
    //   "This is a computer-generated statement.",
    //   pageWidth / 2,
    //   footerLineY - 22,
    //   {
    //     align: "center",
    //   }
    // );

    const statementTitle = `${communityName} - ${eventName}`;

    const titleLines = pdfDocument.splitTextToSize(
      statementTitle,
      pageWidth - PDF_LAYOUT.leftMargin - PDF_LAYOUT.rightMargin
    );

    pdfDocument.text(
      titleLines,
      pageWidth / 2,
      footerLineY - 12,
      {
        align: "center",
      }
    );

    pdfDocument.setDrawColor(220, 220, 220);
    pdfDocument.setLineWidth(0.3);

    pdfDocument.line(
      footerLeft,
      footerLineY,
      footerRight,
      footerLineY
    );

    pdfDocument.setFont("helvetica", "normal");
    pdfDocument.setFontSize(7);
    pdfDocument.setTextColor(110, 110, 110);

    pdfDocument.text(
      "Generated:",
      footerLeft,
      footerLineY + 11
    );

    pdfDocument.setFont("helvetica", "bold");
    pdfDocument.setFontSize(8);
    pdfDocument.setTextColor(70, 70, 70);

    pdfDocument.text(
      "Lupie Service App",
      footerLeft + 60,
      footerLineY + 11
    );

    pdfDocument.setFont("helvetica", "normal");
    pdfDocument.setFontSize(8);
    pdfDocument.setTextColor(0, 102, 204);

    const urlWidth = pdfDocument.getTextWidth(applicationUrlText);

    if (typeof pdfDocument.textWithLink === "function") {
      pdfDocument.textWithLink(
        applicationUrlText,
        footerRight - urlWidth,
        footerLineY + 11,
        {
          url: applicationUrl,
        }
      );
    } else {
      pdfDocument.text(
        applicationUrlText,
        footerRight - urlWidth,
        footerLineY + 11
      );
    }

    pdfDocument.setDrawColor(0, 102, 204);

    pdfDocument.line(
      footerRight - urlWidth,
      footerLineY + 12,
      footerRight,
      footerLineY + 12
    );

    pdfDocument.setTextColor(...palette.black);
  }
};

  const generatePdf = async (mode, includeCommittee = false) => {
    if (!eventId) {
      window.alert("Missing event ID.");
      return;
    }

    try {
      setDownloading(true);

      const pdfDocument = new jsPDF({ unit: "pt", format: "a4" });
      const afterHeaderY = writeHeader(pdfDocument);
      const afterSummaryY = writeSummaryBand(pdfDocument, afterHeaderY);
      const collectionSummaryY = writeSectionHeading(
        pdfDocument,
        "Collection Summary",
        afterSummaryY + 4
      );
      const afterBlockSummaryY = writeBlockWiseSummaryTable(
        pdfDocument,
        collectionSummaryY
      );

      writeExternalCollectionsTable(pdfDocument, afterBlockSummaryY + 16);
      writeExpensesByCategoryTable(pdfDocument, 40, { forceNewPage: true });

      if (mode === "short") {
        if (includeCommittee) writeCommitteeByBlockTable(pdfDocument);
      } else {
        writeResidentCollectionDetails(pdfDocument);
        writeExternalContributionDetails(pdfDocument);
        writeExpenseDetails(pdfDocument);
      }

      addEventLinkToLastPage(pdfDocument);
      addStatementFooter(pdfDocument);
      addPageNumbers(pdfDocument);

      const eventFileName = sanitizeFileName(event?.name || "Summary");
      pdfDocument.save(`Event_${eventFileName}.pdf`);
    } catch (pdfError) {
      console.error("PDF generation error:", pdfError);
      window.alert(pdfError?.message || "Failed to generate PDF.");
    } finally {
      setDownloading(false);
      setShowPdfModal(false);
    }
  };

  const setWorksheetWidths = (worksheet, widths) => {
    worksheet["!cols"] = widths.map((width) => ({ wch: width }));
  };

  const exportExcelFullStatement = () => {
    if (!eventId) {
      window.alert("Missing event ID.");
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();

      const overviewWorksheet = XLSX.utils.aoa_to_sheet([
        ["Field", "Value"],
        ["Community", community?.name || "-"],
        ["Event", event?.name || "-"],
        ["Date", event?.date || "-"],
        ["Total Resident Funds", totalResidentFunds],
        ["Total Other Funds", totalOtherFunds],
        ["Total Funds", totalFunds],
        ["Total Expenses", totalExpenses],
        ["Balance", balance],
      ]);
      setWorksheetWidths(overviewWorksheet, [28, 36]);

      const blockSummaryRows = [
        [
          "Block",
          "Resident Contributions",
          "Donations / Sponsorships",
          "Total",
        ],
        ...blockWiseCollectionSummary.map((row) => [
          row.blockName,
          row.residentAmount,
          row.donationAmount,
          row.total,
        ]),
        [
          "Grand Total",
          blockResidentGrandTotal,
          blockDonationGrandTotal,
          blockCollectionGrandTotal,
        ],
      ];
      const blockSummaryWorksheet = XLSX.utils.aoa_to_sheet(blockSummaryRows);
      setWorksheetWidths(blockSummaryWorksheet, [20, 24, 28, 18]);

      const residentRows = [
        ["Block", "Flat", "Amount", "Method", "Received By", "Receipt", "Created"],
        ...residentFunds.map((fund) => [
          getBlockName(fund) || "-",
          fund.flatLabel || fund.flatNumber || fund.unitNumber || "-",
          Number(fund.amount) || 0,
          fund.method || "-",
          fund.receivedByName || fund.paidByName || "-",
          fund.receiptNo || "-",
          formatTimestamp(fund.createdAt),
        ]),
      ];
      const residentWorksheet = XLSX.utils.aoa_to_sheet(residentRows);
      setWorksheetWidths(residentWorksheet, [16, 16, 16, 18, 26, 20, 24]);

      const externalRows = [
        ["Contributor", "Contribution Type", "Amount", "Method", "Received By", "Receipt", "Created"],
        ...externalContributions.map((fund) => [
          getExternalContributorName(fund),
          getContributionType(fund),
          Number(fund.amount) || 0,
          fund.method || "-",
          fund.receivedByName || "-",
          fund.receiptNo || "-",
          formatTimestamp(fund.createdAt),
        ]),
        ["Grand Total", "", externalContributionTotal, "", "", "", ""],
      ];
      const externalWorksheet = XLSX.utils.aoa_to_sheet(externalRows);
      setWorksheetWidths(externalWorksheet, [30, 28, 18, 18, 26, 20, 24]);

      const expenseSummaryRows = [
        ["Category", "Total Amount", "Entries"],
        ...expensesByCategory.map((group) => [
          group.category,
          group.expenses.reduce(
            (total, expense) => total + (Number(expense.amount) || 0),
            0
          ),
          group.expenses.length,
        ]),
      ];
      const expenseSummaryWorksheet = XLSX.utils.aoa_to_sheet(
        expenseSummaryRows
      );
      setWorksheetWidths(expenseSummaryWorksheet, [24, 18, 12]);

      const expenseRows = [
        ["Purpose", "Category", "Amount", "Paid By", "Receipt", "Created"],
        ...expenses.map((expense) => [
          expense.purpose || "-",
          expense.category || "-",
          Number(expense.amount) || 0,
          expense.paidByName || expense.paidByUid || "-",
          expense.receiptNo || "-",
          formatTimestamp(expense.createdAt),
        ]),
      ];
      const expenseWorksheet = XLSX.utils.aoa_to_sheet(expenseRows);
      setWorksheetWidths(expenseWorksheet, [36, 22, 16, 24, 20, 24]);

      XLSX.utils.book_append_sheet(workbook, overviewWorksheet, "Overview");
      XLSX.utils.book_append_sheet(
        workbook,
        blockSummaryWorksheet,
        "Block_Summary"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        residentWorksheet,
        "Resident_Funds"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        externalWorksheet,
        "External_Contributions"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        expenseSummaryWorksheet,
        "Expenses_By_Category"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        expenseWorksheet,
        "Expense_Details"
      );

      const eventFileName = sanitizeFileName(event?.name || "Full_Statement");
      XLSX.writeFile(workbook, `Event_${eventFileName}.xlsx`);
    } catch (excelError) {
      console.error("Excel export error:", excelError);
      window.alert(excelError?.message || "Failed to export Excel.");
    }
  };

  const loading = loadingFunds || loadingExpenses;

  return (
    <div>
      {fundsError && <Alert variant="danger">{fundsError}</Alert>}
      {expensesError && <Alert variant="danger">{expensesError}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-3">
            <div className="flex-grow-1">
              <div className="text-muted small mb-2">{community?.name || "-"}</div>
              <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                <h5 className="mb-0">{event?.name || "Event"}</h5>
                <Badge bg="info">{event?.status || "-"}</Badge>
              </div>
              <div>
                <strong>Date:</strong> {event?.date || "-"}
              </div>
              {event?.description && (
                <div className="mt-1 text-muted">{event.description}</div>
              )}
            </div>

            <div className="w-100">
              <div className="p-3 rounded-3 border bg-light">
                <Row className="g-3 align-items-stretch">
                  <Col xs={12} sm={6} lg={3}>
                    <div className="h-100 p-2">
                      <div className="small text-muted">Total Funds</div>
                      <div className="fs-5 fw-semibold text-success text-nowrap">
                        {fmtInr(totalFunds)}
                      </div>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        <Badge bg="success">
                          Resident: {fmtInr(totalResidentFunds)}
                        </Badge>
                        <Badge bg="info">Other: {fmtInr(totalOtherFunds)}</Badge>
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} sm={6} lg={3}>
                    <div className="h-100 p-2">
                      <div className="small text-muted">Total Expenses</div>
                      <div className="fs-5 fw-semibold text-danger text-nowrap">
                        {fmtInr(totalExpenses)}
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} sm={6} lg={3}>
                    <div className="h-100 p-2">
                      <div className="small text-muted">Balance</div>
                      <div
                        className={`fs-5 fw-semibold text-nowrap ${
                          balance >= 0 ? "text-success" : "text-danger"
                        }`}
                      >
                        {fmtInr(balance)}
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} sm={6} lg={3}>
                    <Card className="h-100 border-0 shadow-sm">
                      <Card.Body className="py-2">
                        <div className="small text-muted">My Contribution</div>
                        <div className="fs-5 fw-semibold text-success text-nowrap">
                          {fmtInr(myContributionTotal)}
                        </div>
                        <Badge bg={myContributions.length ? "success" : "danger"}>
                          {myContributions.length ? "Paid" : "Not Paid"}
                        </Badge>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <div className="d-flex flex-wrap gap-2 justify-content-end mt-3">
                  <Button
                    type="button"
                    variant="outline-success"
                    size="sm"
                    onClick={exportExcelFullStatement}
                    disabled={!eventId || loading}
                  >
                    Export Excel
                  </Button>

                  <Button
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowPdfModal(true)}
                    disabled={!eventId || loading}
                  >
                    Download PDF
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {loading && (
        <Alert variant="info">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading event overview...
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <strong>Block-wise Collection</strong>
            <Badge bg="success">Total: {fmtInr(blockCollectionGrandTotal)}</Badge>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          <Table bordered hover responsive size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Block</th>
                <th className="text-end text-nowrap">Resident Contributions</th>
                <th className="text-end text-nowrap">Donations / Sponsorships</th>
                <th className="text-end text-nowrap">Total</th>
              </tr>
            </thead>
            <tbody>
              {!blockWiseCollectionSummary.length ? (
                <tr>
                  <td colSpan={4} className="text-center py-3 text-muted">
                    No block-wise collections found.
                  </td>
                </tr>
              ) : (
                blockWiseCollectionSummary.map((row) => (
                  <tr key={row.blockName}>
                    <td>{row.blockName}</td>
                    <td className="text-end text-nowrap">
                      {fmtInr(row.residentAmount)}
                    </td>
                    <td className="text-end text-nowrap">
                      {fmtInr(row.donationAmount)}
                    </td>
                    <td className="text-end text-nowrap fw-bold text-success">
                      {fmtInr(row.total)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {!!blockWiseCollectionSummary.length && (
              <tfoot>
                <tr className="table-light">
                  <th>Grand Total</th>
                  <th className="text-end text-nowrap">
                    {fmtInr(blockResidentGrandTotal)}
                  </th>
                  <th className="text-end text-nowrap">
                    {fmtInr(blockDonationGrandTotal)}
                  </th>
                  <th className="text-end text-nowrap text-success">
                    {fmtInr(blockCollectionGrandTotal)}
                  </th>
                </tr>
              </tfoot>
            )}
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <strong>External Contributions / Sponsorships</strong>
            <Badge bg="info">Total: {fmtInr(externalContributionTotal)}</Badge>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          <Table bordered hover responsive size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Contributor</th>
                <th>Type</th>
                <th className="text-end text-nowrap">Amount</th>
              </tr>
            </thead>
            <tbody>
              {!externalContributions.length ? (
                <tr>
                  <td colSpan={3} className="text-center py-3 text-muted">
                    No external contributions found.
                  </td>
                </tr>
              ) : (
                externalContributions.map((fund) => (
                  <tr key={fund.id}>
                    <td>{getExternalContributorName(fund)}</td>
                    <td>{getContributionType(fund)}</td>
                    <td className="text-end text-nowrap fw-semibold">
                      {fmtInr(Number(fund.amount) || 0)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {!!externalContributions.length && (
              <tfoot>
                <tr className="table-light">
                  <th colSpan={2}>Grand Total</th>
                  <th className="text-end text-nowrap text-success">
                    {fmtInr(externalContributionTotal)}
                  </th>
                </tr>
              </tfoot>
            )}
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <strong>Expense Summary</strong>
            <Badge bg="danger">Total: {fmtInr(totalExpenses)}</Badge>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          <Table bordered hover responsive size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Category</th>
                <th className="text-end text-nowrap">Total Amount</th>
                <th className="text-center text-nowrap">Entries</th>
              </tr>
            </thead>
            <tbody>
              {!expensesByCategory.length ? (
                <tr>
                  <td colSpan={3} className="text-center py-3 text-muted">
                    No expenses found.
                  </td>
                </tr>
              ) : (
                expensesByCategory.map((group) => {
                  const categoryTotal = group.expenses.reduce(
                    (total, expense) => total + (Number(expense.amount) || 0),
                    0
                  );

                  return (
                    <tr key={group.category}>
                      <td>{group.category}</td>
                      <td className="text-end text-nowrap">
                        {fmtInr(categoryTotal)}
                      </td>
                      <td className="text-center">{group.expenses.length}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {currentResident && (
        <Card className="mb-3">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <strong>My Contributions</strong>
              <Badge bg={sortedMyContributions.length ? "success" : "secondary"}>
                Total: {fmtInr(myContributionTotal)}
              </Badge>
            </div>
          </Card.Header>

          <Card.Body>
            <div className="d-none d-md-block">
              <Table bordered hover responsive size="sm" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Amount</th>
                    <th>Type</th>
                    <th>Receipt</th>
                    <th>Method</th>
                    <th>Received By</th>
                  </tr>
                </thead>
                <tbody>
                  {!sortedMyContributions.length ? (
                    <tr>
                      <td colSpan={5} className="text-center py-3 text-muted">
                        No contributions found.
                      </td>
                    </tr>
                  ) : (
                    sortedMyContributions.map((item) => (
                      <tr key={item.id}>
                        <td className="text-success fw-semibold text-nowrap">
                          {fmtInr(Number(item.amount) || 0)}
                        </td>
                        <td>
                          {normalizeValue(item.sourceType) === "other"
                            ? getContributionType(item)
                            : "Resident Contribution"}
                        </td>
                        <td>{item.receiptNo || "-"}</td>
                        <td>{item.method || "-"}</td>
                        <td>{item.receivedByName || "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-md-none">
              {!sortedMyContributions.length ? (
                <Alert variant="warning" className="mb-0">
                  No contributions found.
                </Alert>
              ) : (
                sortedMyContributions.map((item) => (
                  <Card key={item.id} className="mb-2 shadow-sm">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-center gap-2">
                        <strong>Amount</strong>
                        <span className="text-success fw-bold text-nowrap">
                          {fmtInr(Number(item.amount) || 0)}
                        </span>
                      </div>
                      <hr className="my-2" />
                      <div className="mb-1">
                        <strong>Type:</strong>{" "}
                        {normalizeValue(item.sourceType) === "other"
                          ? getContributionType(item)
                          : "Resident Contribution"}
                      </div>
                      <div className="mb-1">
                        <strong>Receipt:</strong> {item.receiptNo || "-"}
                      </div>
                      <div className="mb-1">
                        <strong>Method:</strong> {item.method || "-"}
                      </div>
                      <div>
                        <strong>Received By:</strong> {item.receivedByName || "-"}
                      </div>
                    </Card.Body>
                  </Card>
                ))
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      <Modal
        show={showPdfModal}
        onHide={() => {
          if (!downloading) setShowPdfModal(false);
        }}
        centered
        backdrop={downloading ? "static" : true}
        keyboard={!downloading}
      >
        <Modal.Header closeButton={!downloading}>
          <Modal.Title>Download PDF</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3">Select the report that you want to download.</div>

          <div className="d-grid gap-2">
            <Button
              type="button"
              variant="primary"
              onClick={() => generatePdf("short", true)}
              disabled={downloading}
            >
              {downloading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Building PDF...
                </>
              ) : (
                "Short Summary With Committee Members"
              )}
            </Button>

            <Button
              type="button"
              variant="outline-primary"
              onClick={() => generatePdf("short", false)}
              disabled={downloading}
            >
              Short Summary Without Committee Members
            </Button>

            <Button
              type="button"
              variant="outline-dark"
              onClick={() => generatePdf("full")}
              disabled={downloading}
            >
              Full Statement
            </Button>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowPdfModal(false)}
            disabled={downloading}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
